/**
 * User preferences store (persisted to localStorage via Zustand persist).
 * localStorage key: 'cf-preferences'
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AppMode = 'light' | 'dark' | 'system';
export type AppColorTheme =
  | 'art-deco'
  | 'avilaroffe'
  | 'claude'
  | 'clean-slate'
  | 'corporate'
  | 'ghibli-studio'
  | 'marshmallow'
  | 'marvel'
  | 'neo-brutalism'
  | 'perplexity'
  | 'spotify';
export type AppLanguage = 'en' | 'pt';
export type DateFormat = 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
export type TimeFormat = '12h' | '24h';
export type FirstDayOfWeek = 'sunday' | 'monday';
export type DefaultView = 'queues' | 'agents' | 'chat';

interface PreferencesStore {
  // appearance
  mode: AppMode;
  colorTheme: AppColorTheme;
  // localization
  language: AppLanguage;
  timezone: string;
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  firstDayOfWeek: FirstDayOfWeek;
  // general
  defaultView: DefaultView;

  // setters
  setMode: (mode: AppMode) => void;
  setColorTheme: (theme: AppColorTheme) => void;
  setLanguage: (lang: AppLanguage) => void;
  setTimezone: (tz: string) => void;
  setDateFormat: (fmt: DateFormat) => void;
  setTimeFormat: (fmt: TimeFormat) => void;
  setFirstDayOfWeek: (day: FirstDayOfWeek) => void;
  setDefaultView: (view: DefaultView) => void;

  /** @deprecated use `mode` instead */
  theme: AppMode;
  /** @deprecated use `setMode` instead */
  setTheme: (theme: AppMode) => void;
}

export const usePreferencesStore = create<PreferencesStore>()(
  persist(
    (set) => ({
      // appearance
      mode: 'system',
      colorTheme: 'corporate',
      // localization
      language: 'en',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      dateFormat: 'DD/MM/YYYY',
      timeFormat: '24h',
      firstDayOfWeek: 'monday',
      // general
      defaultView: 'queues',

      // setters
      setMode: (mode) => set({ mode, theme: mode }),
      setColorTheme: (colorTheme) => set({ colorTheme }),
      setLanguage: (language) => set({ language }),
      setTimezone: (timezone) => set({ timezone }),
      setDateFormat: (dateFormat) => set({ dateFormat }),
      setTimeFormat: (timeFormat) => set({ timeFormat }),
      setFirstDayOfWeek: (firstDayOfWeek) => set({ firstDayOfWeek }),
      setDefaultView: (defaultView) => set({ defaultView }),

      // legacy compat
      theme: 'system',
      setTheme: (theme) => set({ theme, mode: theme }),
    }),
    {
      name: 'cf-preferences',
    },
  ),
);
